import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { BaseView, BaseText } from '@/components/base'
import { AppIcons } from '@/components/AppIcons'
import { WalletCurrency } from '@/domain'
import {
  useGetBlockchainsCatalogs,
  BlockchainCatalogsDTO,
} from '@/domain/blockchains'
import { SelectBlockchainModal } from '@/features/currency-deposit/components'
import { NotEnoughFundsModal } from '@/components'
import { currencyUtils } from '@/utils'
import { getReferenceColorsForTheCurrency } from '@/utils/getReferenceColorsForTheCurrency'
import { useUserStore } from '@/store/useUserStore'

const { currencyConditionals } = currencyUtils

interface CryptoAction {
  action: string
  iconName: string
  onPress: (currency: WalletCurrency) => void
}

interface CryptoActionsProps {
  walletSelected: WalletCurrency
}

export const CryptoActions: React.FC<CryptoActionsProps> = ({
  walletSelected,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const hasIdentityVerified = useUserStore(
    (state) => state.userAuthenticated?.hasIdentityVerified,
  )
  // State for blockchain selection modal
  const [isBlockchainModalOpen, setIsBlockchainModalOpen] = useState(false)

  // State for insufficient funds modal
  const [isNotEnoughFundsModalOpen, setIsNotEnoughFundsModalOpen] =
    useState(false)

  // Get colors for current currency
  const currencyColors = getReferenceColorsForTheCurrency({
    currencyCode: walletSelected.code || 'USD',
  })

  // Get blockchains for currency
  const { data: blockchainsData } = useGetBlockchainsCatalogs({
    currency_isin: walletSelected.code || '',
  })

  // Use only data from API
  const blockchains =
    (blockchainsData as { blockchains: BlockchainCatalogsDTO[] })
      ?.blockchains || []

  const handleSend = (currency: WalletCurrency) => {
    navigate('/send-request', {
      state: {
        currency,
        mode: 'send',
        type: 'send',
        cryptoCode: currency.code,
      },
    })
  }

  const handleReceive = (currency: WalletCurrency) => {
    // If multiple blockchains - show modal
    if (blockchains.length > 1) {
      setIsBlockchainModalOpen(true)
    } else if (blockchains.length === 1) {
      // If single blockchain - navigate directly
      navigateToCurrencyDeposit(currency, blockchains[0])
    } else {
      // If no blockchains - navigate without selected blockchain
      navigateToCurrencyDeposit(currency, undefined)
    }
  }

  const navigateToCurrencyDeposit = (
    currency: WalletCurrency,
    selectedBlockchain?: BlockchainCatalogsDTO,
  ) => {
    navigate('/currency-deposit', {
      state: {
        currency,
        blockchains: blockchains,
        selectedBlockchain,
      },
    })
  }

  const handleBlockchainSelect = (blockchain: BlockchainCatalogsDTO) => {
    setIsBlockchainModalOpen(false)
    navigateToCurrencyDeposit(walletSelected, blockchain)
  }

  const handleConvert = (currency: WalletCurrency) => {
    const { isUsdt } = currencyConditionals(currency.code || '')

    navigate('/conversion', {
      state: {
        debitCurrencyCode: currency.code,
        creditCurrencyCode: isUsdt ? 'BTC' : undefined,
      },
    })
  }

  const handleWithdraw = (currency: WalletCurrency) => {
    // TODO: Add analytics
    if (!hasIdentityVerified) {
      navigate('/validations-by-feature?feature=withdraw')
      return
    }
    navigate('/withdraw', {
      state: {
        currency,
        cryptoCode: currency.code,
        from: '/currency-details',
      },
    })
  }

  const cryptoActions: CryptoAction[] = [
    {
      action: 'send',
      iconName: 'arrowBigRight',
      onPress: handleSend,
    },
    {
      action: 'receive',
      iconName: 'qrCode',
      onPress: handleReceive,
    },
    {
      action: 'convert',
      iconName: 'exchange',
      onPress: handleConvert,
    },
    {
      action: 'withdraw',
      iconName: 'arrowUpUnderline',
      onPress: handleWithdraw,
    },
  ]

  const handleAddMoney = () => {
    setIsNotEnoughFundsModalOpen(false)

    navigate('/add-money', {
      state: {
        currency: walletSelected,
        selectedCurrency: walletSelected,
      },
    })
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          gap: '8px',
        }}
      >
        {cryptoActions.map(({ action, iconName, onPress }) => {
          return (
            <BaseView
              key={action}
              center
              py={12}
              gap={10}
              rounded={24}
              style={{
                backgroundColor: currencyColors.buttonBGRefColor,
                flex: 1,
                cursor: 'pointer',
              }}
              onClick={() => {
                onPress(walletSelected)
              }}
            >
              <AppIcons
                name={iconName}
                unthemedColor={currencyColors.buttonIconRefColor}
                size={26}
              />

              <BaseText
                fontFamily="semibold"
                fontSize={15}
                color="label.buttonPrimaryEnabled"
                textAlign="center"
                style={{
                  color: '#FFFFFF',
                }}
              >
                {t(`form:actions.${action}`)}
              </BaseText>
            </BaseView>
          )
        })}
      </div>

      {/* Blockchain selection modal */}
      <SelectBlockchainModal
        isOpen={isBlockchainModalOpen}
        onClose={() => setIsBlockchainModalOpen(false)}
        onSelect={handleBlockchainSelect}
        blockchains={blockchains}
        selectedBlockchain={undefined}
        currencyCode={walletSelected.code || ''}
      />

      {/* Insufficient funds modal */}
      <NotEnoughFundsModal
        isOpen={isNotEnoughFundsModalOpen}
        onClose={() => setIsNotEnoughFundsModalOpen(false)}
        onAddMoney={handleAddMoney}
      />
    </>
  )
}
